"use client";

import { useRef, useState } from "react";
import {
  CopyButton,
  Notice,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  CARD,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  type SatBest,
  type SatIssue,
  type SatSitting,
  type SatSuperscore,
  satScoreIssue,
  satSuperscore,
} from "@/lib/tool-kit/grades/sat";

/** English strings. A translated content file overrides these through `ui`. */
export const SAT_SUPERSCORE_CALCULATOR_STRINGS = {
  testsHeading: "Your SAT test dates",
  testLabel: "Test {n}",
  testWithDate: "{n} ({date})",
  dateLabel: "Test date (optional)",
  rwLabel: "Reading and Writing (200–800)",
  mathLabel: "Math (200–800)",
  removeTest: "Remove",
  removeTestAria: "Remove test {n}",
  addTest: "Add another test date",
  issues: {
    "not-a-number": "enter both section scores.",
    "out-of-range": "section scores run from 200 to 800.",
    "not-multiple-of-10": "section scores come in steps of 10.",
  },
  skipped: "Test {n} is left out for now — {issue}",
  results: "Result",
  superscore: "Superscore",
  bestRw: "Best Reading and Writing",
  bestMath: "Best Math",
  fromTest: "{score} · test {n}",
  bestSingle: "Best single test",
  gain: "Gain from superscoring",
  working: "{rw} + {math} = {superscore}",
  enterTests: "Enter both section scores for at least one test date.",
  policyNote:
    "Only a college that superscores combines sections from different dates — check each college's score-use policy.",
  copyResult: "Copy result",
  copied: "Copied!",
  summary:
    "SAT superscore {superscore} (Reading and Writing {rw}, Math {math})",
} as const;

type Strings = typeof SAT_SUPERSCORE_CALCULATOR_STRINGS;
type Format = ReturnType<typeof useToolNumber>;

/** Scores print as College Board writes them: 1390, never 1,390. */
const SCORE = { useGrouping: false } as const;

interface SittingRow {
  id: number;
  date: string;
  readingWriting: string;
  math: string;
}

const INITIAL_ROWS: SittingRow[] = [
  { id: 1, date: "", readingWriting: "640", math: "690" },
  { id: 2, date: "", readingWriting: "700", math: "670" },
];

function readSitting(
  row: SittingRow
): { sitting: SatSitting } | { issue: SatIssue } {
  const readingWriting = parseNumberInput(row.readingWriting);
  const math = parseNumberInput(row.math);
  const issue = satScoreIssue(readingWriting) ?? satScoreIssue(math);
  if (issue !== null || readingWriting === null || math === null) {
    return { issue: issue ?? "not-a-number" };
  }
  return { sitting: { readingWriting, math } };
}

/** A user-entered date for display. Only called once someone has typed one. */
function formatDate(value: string, locale: string): string {
  const parsed = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeZone: "UTC",
  }).format(parsed);
}

function ResultPanel({
  result,
  label,
  strings,
  fmt,
}: {
  result: SatSuperscore;
  label: (index: number) => string;
  strings: Strings;
  fmt: Format;
}) {
  const best = (entry: SatBest) =>
    t(strings.fromTest, {
      score: fmt(entry.score, SCORE),
      n: label(entry.sitting),
    });
  const gain = result.superscore - result.bestSingle.score;
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.superscore}
          value={fmt(result.superscore, SCORE)}
        />
        <StatCard label={strings.bestRw} value={best(result.readingWriting)} />
        <StatCard label={strings.bestMath} value={best(result.math)} />
        <StatCard label={strings.bestSingle} value={best(result.bestSingle)} />
        <StatCard
          label={strings.gain}
          value={gain > 0 ? `+${fmt(gain)}` : fmt(gain)}
        />
      </StatGrid>
      <p className="rounded-lg border border-border bg-card p-3 font-mono text-muted-foreground text-sm">
        {t(strings.working, {
          rw: fmt(result.readingWriting.score, SCORE),
          math: fmt(result.math.score, SCORE),
          superscore: fmt(result.superscore, SCORE),
        })}
      </p>
      <p className="text-muted-foreground text-sm">{strings.policyNote}</p>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResult}
        small
        text={t(strings.summary, {
          superscore: fmt(result.superscore, SCORE),
          rw: fmt(result.readingWriting.score, SCORE),
          math: fmt(result.math.score, SCORE),
        })}
      />
    </>
  );
}

export default function SatSuperscoreCalculatorWidget() {
  const strings = useToolUi(SAT_SUPERSCORE_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const idCounter = useRef(INITIAL_ROWS.length);
  const [rows, setRows] = useState<SittingRow[]>(INITIAL_ROWS);

  const read = rows.map(readSitting);
  const valid = read.flatMap((entry, index) =>
    "sitting" in entry ? [{ sitting: entry.sitting, index }] : []
  );
  const result = satSuperscore(valid.map((entry) => entry.sitting));
  const label = (validIndex: number) => {
    const index = valid[validIndex]?.index ?? validIndex;
    const row = rows[index];
    const n = fmt(index + 1);
    return row?.date
      ? t(strings.testWithDate, { n, date: formatDate(row.date, locale) })
      : n;
  };

  function update(id: number, patch: Partial<SittingRow>) {
    setRows((all) =>
      all.map((row) => (row.id === id ? { ...row, ...patch } : row))
    );
  }

  function addRow() {
    idCounter.current += 1;
    const id = idCounter.current;
    setRows((all) => [...all, { id, date: "", readingWriting: "", math: "" }]);
  }

  return (
    <div className="space-y-6">
      <h2 className={WIDGET_H2}>{strings.testsHeading}</h2>
      {rows.map((row, index) => (
        <div className={`${CARD} space-y-4`} key={row.id}>
          <div className="flex items-center justify-between gap-3">
            <h3 className={WIDGET_H3}>
              {t(strings.testLabel, { n: index + 1 })}
            </h3>
            <button
              aria-label={t(strings.removeTestAria, { n: index + 1 })}
              className={BUTTON_SMALL}
              disabled={rows.length <= 1}
              onClick={() =>
                setRows((all) => all.filter((each) => each.id !== row.id))
              }
              type="button"
            >
              {strings.removeTest}
            </button>
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <TextField
              id={`sat-date-${row.id}`}
              label={strings.dateLabel}
              onChange={(date) => update(row.id, { date })}
              type="date"
              value={row.date}
            />
            <TextField
              id={`sat-rw-${row.id}`}
              label={strings.rwLabel}
              numeric
              onChange={(readingWriting) => update(row.id, { readingWriting })}
              value={row.readingWriting}
            />
            <TextField
              id={`sat-math-${row.id}`}
              label={strings.mathLabel}
              numeric
              onChange={(math) => update(row.id, { math })}
              value={row.math}
            />
          </div>
        </div>
      ))}
      <button className={BUTTON_SECONDARY} onClick={addRow} type="button">
        {strings.addTest}
      </button>

      <section aria-live="polite" className={`${RESULT_CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {rows.map((row, index) => {
          const entry = read[index];
          return "issue" in entry ? (
            <Notice key={row.id}>
              {t(strings.skipped, {
                n: index + 1,
                issue: strings.issues[entry.issue],
              })}
            </Notice>
          ) : null;
        })}
        {result ? (
          <ResultPanel
            fmt={fmt}
            label={label}
            result={result}
            strings={strings}
          />
        ) : (
          <Notice>{strings.enterTests}</Notice>
        )}
      </section>
    </div>
  );
}
